import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';

const ClientProfile = ({ onProfileUpdate, onCancel }) => {
  const { accessToken: token, user, setUser } = useAuth();
  const [form, setForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    headline: "",
    about: "",
    profile_picture: null,
  });

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`https://quirkleai.com/api/me/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setForm({
        username: data.username || "",
        email: data.email || "",
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        headline: data.headline || "",
        about: data.about || "",
        profile_picture: data.profile_picture || null,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Could not load profile data.");
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      formData.append('headline', form.headline);
      formData.append('about', form.about);
      if (form.profile_picture instanceof File) {
        formData.append('profile_picture', form.profile_picture);
      }

      const response = await fetch(`https://quirkleai.com/api/me/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      toast.success("Profile updated successfully!");
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Edit Profile</h1>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="h-6 w-6" />
        </Button>
      </div>

      <div className="space-y-6">
        {/* Profile Form Card */}
        <Card className="bg-white shadow-sm border">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-800 border-b pb-2">Personal Information</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input name="first_name" value={form.first_name} onChange={handleChange} className="mt-1 block w-full border rounded-md px-3 py-2 text-sm" placeholder="Your first name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input name="last_name" value={form.last_name} onChange={handleChange} className="mt-1 block w-full border rounded-md px-3 py-2 text-sm" placeholder="Your last name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Headline</label>
              <input name="headline" value={form.headline} onChange={handleChange} className="mt-1 block w-full border rounded-md px-3 py-2 text-sm" placeholder="A short headline about you" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">About</label>
              <textarea name="about" value={form.about} onChange={handleChange} rows="4" className="mt-1 block w-full border rounded-md px-3 py-2 text-sm" placeholder="Tell us something about yourself..." />
            </div>
          </CardContent>
        </Card>

        {/* Profile Picture Card */}
        <Card className="bg-white shadow-sm border">
          <CardContent className="py-6 space-y-4">
            <label className="block text-sm font-medium text-gray-700">Profile Picture</label>
            {form.profile_picture && typeof form.profile_picture === 'string' && (
              <div className="my-2">
                <img style={{ width: 80, height: 80 }} src={`http://localhost:8000${form.profile_picture}`} alt="Current Profile" className="rounded-full object-cover" />
              </div>
            )}
            <input type="file" name="profile_picture" onChange={(e) => setForm({ ...form, profile_picture: e.target.files[0] })} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100" />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;